import React, { useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import loginBg from "@/assets/tria-login-bg.jpg";

const TriaLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="triaGrad" x1="32" y1="4" x2="32" y2="60" gradientUnits="userSpaceOnUse">
        <stop stopColor="#8fd0ff" />
        <stop offset="0.55" stopColor="#3b82f6" />
        <stop offset="1" stopColor="#1e4fb0" />
      </linearGradient>
    </defs>
    <g stroke="url(#triaGrad)" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M29 10 L32 6 L35 10" />
      <path d="M17 15 L20 11 L23 15" />
      <path d="M41 15 L44 11 L47 15" />
      <path d="M32 6 V23" />
      <path d="M20 11 V22" />
      <path d="M44 11 V22" />
      <path d="M19 23 Q20 24 24 24 H40 Q44 24 45 23" />
      <path d="M32 23 V55" />
      <path d="M27 55 H37" />
    </g>
  </svg>
);

export default function Login() {
  const { loadMe } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "login") {
        await api.post("/auth/login", { email: form.email, password: form.password });
      } else {
        await api.post("/auth/register", { name: form.name, email: form.email, password: form.password });
      }
      await loadMe();
      const back = localStorage.getItem("sapientia_invite_return");
      if (back) { localStorage.removeItem("sapientia_invite_return"); navigate(back); }
      else navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div
        className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden"
        style={{ backgroundImage: `url(${loginBg})`, backgroundSize: "cover", backgroundPosition: "center" }}
        data-testid="login-brand-panel"
      >
        <div className="absolute inset-0 bg-[#060f24]/45" />
        <div className="relative z-10 flex items-center gap-4">
          <TriaLogo className="h-14 w-14 drop-shadow-[0_0_12px_rgba(59,130,246,0.55)]" />
          <div>
            <div className="font-display text-4xl font-extrabold tracking-[0.3em] leading-none">TRIA</div>
            <div className="mt-1.5 text-xs text-white/70 tracking-wide">Inteligência financeira para varejo e brechós</div>
          </div>
        </div>

        <div className="relative z-10 max-w-lg">
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-[1.1] tracking-tight">
            Gestão inteligente <span className="text-[#4f8cf5]">de margem</span> por lotes
          </h1>
          <p className="mt-5 text-white/70 text-base leading-relaxed">
            O TRIA transforma compras, precificação, remarcações e vendas em indicadores claros de resultado. Nosso método conecta Custos, Margem e Faturamento para mostrar o que cada lote gera, quanto da operação realmente sobra e qual faturamento sustenta o lucro do negócio.
          </p>
          <div className="mt-8">
            <div className="h-1 w-12 rounded-full bg-[#4f8cf5] mb-3" />
            <div className="text-sm text-white/60 tracking-[0.22em] font-num">CUSTOS · MARGEM · FATURAMENTO</div>
          </div>
        </div>

        <div className="relative z-10 text-sm text-white/45">Developed by Sapientia Consultores Associados</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <TriaLogo className="h-10 w-10" />
            <span className="font-display text-2xl font-extrabold tracking-[0.28em]">TRIA</span>
          </div>
          <h2 className="font-display text-2xl font-bold">Acesse sua conta</h2>
          <p className="text-muted-foreground mt-1 mb-6">Inteligência financeira para varejo e brechós</p>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="login" data-testid="auth-tab-login">Entrar</TabsTrigger>
              <TabsTrigger value="register" data-testid="auth-tab-register">Criar conta</TabsTrigger>
            </TabsList>

            <form onSubmit={submit} className="space-y-4">
              <TabsContent value="register" className="mt-0">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input id="name" value={form.name} onChange={set("name")} required={tab === "register"} data-testid="auth-register-name" />
                </div>
              </TabsContent>
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input id="email" type="email" value={form.email} onChange={set("email")} required data-testid="auth-login-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input id="password" type="password" value={form.password} onChange={set("password")} required data-testid="auth-login-password" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="auth-submit">
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {tab === "login" ? "Entrar no sistema" : "Criar conta"}
              </Button>
            </form>
          </Tabs>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px bg-border flex-1" />
            <span className="text-xs text-muted-foreground">ou</span>
            <div className="h-px bg-border flex-1" />
          </div>

          <Button variant="outline" className="w-full" onClick={google} data-testid="auth-google-button">
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continuar com Google
          </Button>
        </div>
      </div>
    </div>
  );
}
